import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Grid, CircularProgress, 
  Alert, Snackbar, Chip, Card, CardContent, IconButton,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  InputLabel, Select, MenuItem, ListItemText, Checkbox, Tab, Tabs,
  TextField
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { collectionsAPI, booksAPI } from '../services/api';
import CollectionHeaderCard from '../components/collections/CollectionHeaderCard';

export default function CollectionDetail() {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const [collection, setCollection] = useState(null);
  const [allBooks, setAllBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [bookSearchDialog, setBookSearchDialog] = useState(false);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [currentBookIds, setCurrentBookIds] = useState([]);
  const [generateCoverDialog, setGenerateCoverDialog] = useState(false);
  const [coverPrompt, setCoverPrompt] = useState('');
  const [generatingCover, setGeneratingCover] = useState(false);

  useEffect(() => {
    const fetchCollectionDetails = async () => {
      try {
        setLoading(true);
        const [collectionResponse, booksResponse] = await Promise.all([
          collectionsAPI.getCollectionById(collectionId),
          booksAPI.getAllBooks(1, 100) // Get a large batch of books
        ]);

        const collectionData = collectionResponse.data;
        setCollection(collectionData);
        
        // Extract book IDs from collection data
        const bookIds = collectionData.books?.map(book => book.id) || [];
        setCurrentBookIds(bookIds);
        
        // Set all available books for selection
        setAllBooks(booksResponse.data?.data || []);
        
      } catch (error) {
        console.error('Error fetching collection details:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load collection details',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCollectionDetails();
  }, [collectionId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleBooksChange = (event) => {
    const { value } = event.target;
    setSelectedBooks(value);
  };

  const handleBookDialogOpen = () => {
    setSelectedBooks(currentBookIds || []);
    setBookSearchDialog(true);
  };

  const handleBookDialogClose = () => {
    setBookSearchDialog(false);
  };

  // Handler for updating collection state when the header card makes changes
  const handleCollectionUpdate = (updatedCollection) => {
    setCollection(updatedCollection);
  };

  const handleBookSelectionConfirm = async () => {
    try {
      setLoading(true);
      
      // Identify books to add (in selectedBooks but not in currentBookIds)
      const booksToAdd = selectedBooks.filter(id => !currentBookIds.includes(id));
      
      // Identify books to remove (in currentBookIds but not in selectedBooks)
      const booksToRemove = currentBookIds.filter(id => !selectedBooks.includes(id));
      
      // Add new books if needed
      if (booksToAdd.length > 0) {
        await collectionsAPI.addBooksToCollection(collectionId, booksToAdd);
      }
      
      // Remove books if needed
      if (booksToRemove.length > 0) {
        await collectionsAPI.removeBooksFromCollection(collectionId, booksToRemove);
      }
      
      // Update local state
      setCurrentBookIds(selectedBooks);
      
      // Refresh collection data to see changes
      const { data } = await collectionsAPI.getCollectionById(collectionId);
      setCollection(data);
      
      setSnackbar({
        open: true,
        message: 'Collection books updated successfully',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Error updating collection books:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update collection books',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setBookSearchDialog(false);
    }
  };

  const handleRemoveBook = async (bookId, event) => {
    // Stop event propagation to prevent navigation to book details
    event.stopPropagation();
    
    try {
      setLoading(true);
      
      // Use the API to remove just this one book
      await collectionsAPI.removeBooksFromCollection(collectionId, [bookId]);
      
      // Update the currentBookIds state
      setCurrentBookIds(prev => prev.filter(id => id !== bookId));
      
      // Refresh collection data to see changes
      const { data } = await collectionsAPI.getCollectionById(collectionId);
      setCollection(data);
      
      setSnackbar({
        open: true,
        message: 'Book removed from collection successfully',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Error removing book from collection:', error);
      setSnackbar({
        open: true,
        message: 'Failed to remove book from collection',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateCover = async () => {    
    try {
      setGeneratingCover(true);
      
      // Call the API to generate a new cover
      await collectionsAPI.generateCollectionCover(collectionId, {
        prompt: coverPrompt
      });
      
      // Refresh collection data to get the updated cover
      const { data } = await collectionsAPI.getCollectionById(collectionId);
      setCollection(data);
      
      setSnackbar({
        open: true,
        message: 'Cover is being generated. It may take a minute to appear.',
        severity: 'success'
      });
      
      // Close the dialog
      setGenerateCoverDialog(false);
      setCoverPrompt('');
      
    } catch (error) {
      console.error('Error generating cover:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate cover image',
        severity: 'error'
      });
    } finally {
      setGeneratingCover(false);
    }
  };

  if (loading && !collection) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/collections')} 
        variant="outlined" 
        sx={{ mb: 3 }}
      >
        Back to Collections
      </Button>

      {collection && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <CollectionHeaderCard 
              collection={collection} 
              onCollectionUpdate={handleCollectionUpdate}
              setSnackbar={setSnackbar}
            />
            
            <Button 
              variant="outlined"
              color="secondary"
              startIcon={<AutoFixHighIcon />}
              onClick={() => setGenerateCoverDialog(true)}
            >
              Regenerate Cover
            </Button>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab icon={<CollectionsBookmarkIcon />} label="Collection Details" />
              <Tab icon={<MenuBookIcon />} label="Books" />
            </Tabs>
          </Box>
          
          {/* Contents based on selected tab */}
          <Box sx={{ mb: 3 }}>
            {tabValue === 0 && (
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Collection Information</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">ID</Typography>
                        <Typography variant="body2">{collection.id}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                        <Typography variant="body2">
                          {collection.createdAt ? new Date(collection.createdAt).toLocaleString() : 'N/A'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            )}
            
            {tabValue === 1 && (
              <Paper elevation={2} sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Books in Collection</Typography>
                  <Button 
                    variant="contained"
                    onClick={handleBookDialogOpen}
                  >
                    Manage Books
                  </Button>
                </Box>
                
                {!collection.books?.length ? (
                  <Alert severity="info">
                    No books in this collection. Add books using the Manage Books button.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {collection.books.map(book => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={book.id}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            height: '100%',
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 3 },
                            position: 'relative' // Added for absolute positioning of the delete button
                          }}
                          onClick={() => navigate(`/books/${book.id}`)}
                        >
                          {/* Add delete icon button with hover effect */}
                          <IconButton
                            size="small"
                            color="error"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              backgroundColor: 'rgba(255, 255, 255, 0.7)',
                              opacity: 0, // Hidden by default
                              transition: 'opacity 0.2s',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              },
                              // Parent hover selector to show the button on card hover
                              '.MuiCard-root:hover &': {
                                opacity: 1,
                              }
                            }}
                            onClick={(e) => handleRemoveBook(book.id, e)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          
                          <Box 
                            component="img"
                            src={book.cover}
                            alt={book.title}
                            sx={{ 
                              height: 200, 
                              objectFit: 'cover',
                              width: '100%'
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://placehold.co/400x600?text=No+Cover';
                            }}
                          />
                          <CardContent>
                            <Typography variant="subtitle1" noWrap>{book.title}</Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {book.author}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Paper>
            )}
          </Box>

          {/* Book selection dialog */}
          <Dialog 
            open={bookSearchDialog} 
            onClose={handleBookDialogClose}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Manage Collection Books</DialogTitle>
            <DialogContent>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Select Books</InputLabel>
                <Select
                  multiple
                  value={selectedBooks}
                  onChange={handleBooksChange}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const book = allBooks.find(b => b.id === value);
                        return <Chip key={value} label={book ? book.title : value} />;
                      })}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 4.5 + 8,
                        width: 250,
                      },
                    },
                  }}
                >
                  {allBooks.map((book) => (
                    <MenuItem key={book.id} value={book.id}>
                      <Checkbox checked={selectedBooks.indexOf(book.id) > -1} />
                      <ListItemText primary={book.title} secondary={book.author} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleBookDialogClose}>Cancel</Button>
              <Button onClick={handleBookSelectionConfirm} variant="contained">
                Save Books
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add the dialog for cover generation */}
          <Dialog
            open={generateCoverDialog}
            onClose={() => !generatingCover && setGenerateCoverDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Generate Collection Cover</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                id="prompt"
                label="Cover Image Prompt"
                type="text"
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                value={coverPrompt}
                onChange={(e) => setCoverPrompt(e.target.value)}
                disabled={generatingCover}
                placeholder="Describe what you want on the cover, e.g.: A bookshelf filled with books about leadership and management in a modern office setting"
                helperText="Be specific about the content, style, colors, and mood you want for the cover"
              />
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setGenerateCoverDialog(false)} 
                disabled={generatingCover}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleRegenerateCover} 
                color="primary" 
                variant="contained"
                disabled={generatingCover}
                startIcon={generatingCover && <CircularProgress size={16} />}
              >
                {generatingCover ? 'Generating...' : 'Generate Cover'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
